import { css, cva } from '@styled-system/css';
import { CreditCard, User } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingsData } from '@/features/settings/hooks/useSettingsData';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';

import { AccountTab } from './AccountTab';
import { PaymentTab } from './PaymentTab';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'account' | 'payment';

interface TabDef {
  key: TabType;
  label: string;
  icon: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

const tabsNav = css({
  display: 'flex',
  gap: '6',
  alignItems: 'center',
});

const tabButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '3',
    paddingX: '0',
    bg: 'transparent',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    fontFamily: 'sans',
    fontSize: 'bodySm',
    fontWeight: 'medium',
    cursor: 'pointer',
    transition: 'color 120ms ease, border-color 120ms ease',
    color: 'text.secondary',
    _hover: { color: 'text.primary' },
  },
  variants: {
    active: {
      true: { color: 'brand.primary', borderBottomColor: 'brand.primary' },
      false: {},
    },
    disabled: {
      true: {
        color: 'text.tertiary',
        cursor: 'not-allowed',
        opacity: 0.55,
        _hover: { color: 'text.tertiary' },
      },
      false: {},
    },
  },
  defaultVariants: { active: false, disabled: false },
});

export const SettingsModal: FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const { settings } = useSettingsData();
  const { t } = useTranslation();

  const tabs: TabDef[] = [
    {
      key: 'account',
      label: t('settings.tabs.account'),
      icon: <User size={14} strokeWidth={1.75} />,
      content: <AccountTab />,
    },
    {
      key: 'payment',
      label: t('settings.tabs.payment'),
      icon: <CreditCard size={14} strokeWidth={1.75} />,
      content: <PaymentTab />,
      disabled: !settings.isPaymentTabAvailable,
    },
  ];

  const activeContent = tabs.find((tab) => tab.key === activeTab)?.content;

  const tabsHeader = (
    <div className={tabsNav}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => !tab.disabled && setActiveTab(tab.key)}
          disabled={tab.disabled}
          className={tabButton({ active: activeTab === tab.key, disabled: tab.disabled ?? false })}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('settings.title')}
      size="xl"
      height="600px"
      extra={tabsHeader}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {activeContent}
    </Modal>
  );
};
